import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Box, Paper, TextField } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import TareasTable from './TareasTable';
import TareasForm from './TareasForm';
import DeleteTareasDialog from './DeleteTareasDialog';
import {
    fetchTareas,
    createTarea,
    updateTarea,
    deleteTarea
} from '../../services/empleados.api';
import { useSnackbar } from 'notistack';
import Navbar from '../../../../../Navbar';
import Title from '../../../../../Title';
import { useDebounce } from '../../../../../../Hooks/UseDebounce';

const TareasPage = () => {
    const [tareas, setTareas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [currentTarea, setCurrentTarea] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({
        nombre: '',
        categoria_id: ''
        
    });
    const { enqueueSnackbar } = useSnackbar();
    
    const debouncedFilters = useDebounce(filters, 500);

    useEffect(() => {
        loadTareas({ 
            page: page + 1, 
            rowsPerPage: rowsPerPage, 
            ...debouncedFilters 
        });
    }, [page, rowsPerPage, debouncedFilters]);

    const loadTareas = async (params = {}) => {
        setLoading(true);
        try {
            const data = await fetchTareas(params);
            if (data.data) {
                setTareas(data.data);
                setTotal(parseInt(data.total) || data.data.length);
            } else {
                setTareas(data || []);
                setTotal(data.length || 0);
            }
        } catch (error) {
            console.error("Error loading tareas:", error);
            enqueueSnackbar('Error al cargar tareas', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (column, value) => {
        setFilters(prev => ({ ...prev, [column]: value }));
    };

    useEffect(() => {
        setPage(0);
    }, [debouncedFilters]);

    const handleOpenCreate = () => {
        setCurrentTarea(null);
        setFormOpen(true);
    };

    const handleOpenEdit = (tarea) => {
        setCurrentTarea(tarea);
        setFormOpen(true);
    };

    const handleOpenDelete = (tarea) => {
        setCurrentTarea(tarea);
        setDeleteOpen(true);
    };

    const getUser = () => {
        return localStorage.getItem("user") || "unknown";
    };

    const handleFormSubmit = async (formData) => {
        try {
            const user = getUser();
            const payload = { ...formData, username: user };

            if (currentTarea) {
                await updateTarea(currentTarea.id_tarea || currentTarea.id, payload);
                enqueueSnackbar('Tarea actualizada correctamente', { variant: 'success' });
            } else {
                await createTarea(payload);
                enqueueSnackbar('Tarea creada correctamente', { variant: 'success' });
            }
            setFormOpen(false);
            loadTareas({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error saving tarea:", error);
            enqueueSnackbar('Error al guardar la tarea', { variant: 'error' });
        }
    };

    const handleDeleteConfirm = async () => {
        if (!currentTarea) return;
        try {
            await deleteTarea(currentTarea.id_tarea || currentTarea.id);
            enqueueSnackbar('Tarea eliminada correctamente', { variant: 'success' });
            setDeleteOpen(false);
            loadTareas({ page: page + 1, rowsPerPage: rowsPerPage, ...filters });
        } catch (error) {
            console.error("Error deleting tarea:", error);
            enqueueSnackbar('Error al eliminar la tarea', { variant: 'error' });
        }
    };

    return (
        <Container maxWidth="xl" sx={{  mb: 4 }}>
            <Navbar />
            <Title titulo="Gestión de Tareas" handleClose={() => window.history.back()} />
            
            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} mb={1}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)',
                        px: 3,
                        '&:hover': {
                            boxShadow: '0 6px 20px rgba(0,118,255,0.23)',
                        }
                    }}
                >
                    Nueva Tarea
                </Button>
            </Box>

            <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <TareasTable
                    data={tareas}
                    loading={loading}
                    onEdit={handleOpenEdit}
                    onDelete={handleOpenDelete}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    total={total}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
            </Paper>

            <TareasForm
                open={formOpen}
                handleClose={() => setFormOpen(false)}
                handleSubmit={handleFormSubmit}
                initialData={currentTarea}
            />

            <DeleteTareasDialog
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                handleConfirm={handleDeleteConfirm}
                itemName={currentTarea?.nombre}
            />
        </Container>
    );
};

export default TareasPage;
